<!--
Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
Unauthorized use, copying, distribution, displaying, or public performance
of this file, via any medium, is strictly prohibited. All information
contained herein is proprietary and confidential and may not be shared
with any third party without the express written consent of Reciprocity, Inc.
-->

<template>
  <div>
    <div v-if="featureFlags.questionnaire_improvements_external_recipients" class="recipient-list">
      <zen-form-group label="Emails">
        <b-form-textarea
          v-model="text"
          :class="{ invalid: validated && !value.length }"
          placeholder="Type or paste a list of emails separated by comma"
          @paste.prevent="onBulkPaste"
          @blur="onBulkBlur"
        />
        <div v-if="invalid.length" class="warning">
          There are some invalid recipient entries: {{ invalid.join(', ') }}
        </div>
      </zen-form-group>
    </div>

    <div v-else>
      <div class="recipient-list">
        <b-form-row>
          <b-col cols="5">
            <label class="pt-0">
              <span>Name</span>
            </label>
          </b-col>
          <b-col cols="5">
            <label class="pt-0">
              <span>E-mail</span>
            </label>
          </b-col>
          <b-col cols="2" />
        </b-form-row>

        <b-form-row v-for="(recipient, index) in value" :key="index">
          <b-col cols="5">
            <b-form-input
              :value="recipient.name"
              placeholder="Enter name"
              type="text"
              required
              @input="editRecipient(index, 'name', $event)"
            />
          </b-col>
          <b-col cols="5">
            <b-form-input
              :value="recipient.email"
              placeholder="Enter e-mail"
              type="email"
              required
              @input="editRecipient(index, 'email', $event)"
            />
          </b-col>
          <b-col cols="2">
            <a :class="deleteButtonClass" href="#" @click.prevent="removeRecipient(index)">
              Remove
            </a>
          </b-col>
        </b-form-row>
      </div>
      <a class="d-inline-block mt-2 mb-3" href="#" @click.prevent="addRecipient()">
        <i class="fa fa-plus" />
        Add more
      </a>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { BFormTextarea, BFormInput, BCol, BFormRow } from 'bootstrap-vue';
import { ZenFormGroup } from 'zenComponents';
import { cloneDeep } from 'lodash';

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
const RECIPIENT_REGEX = /^"(.+)" <(\S+@\S+\.\S+)>$/;

const getBulkRecipientData = text => {
  return text
    .split('\n')
    .reduce((recipients, line) => {
      return recipients.concat(line.split(','));
    }, [])
    .reduce(
      (result, item) => {
        // remove extra blank spaces
        const recipient = item.trim();

        // filter out blank lines
        if (!recipient) return result;

        if (RECIPIENT_REGEX.test(recipient)) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const [_, name, email] = RECIPIENT_REGEX.exec(recipient);
          result.recipients.push({ name, email });
        } else if (EMAIL_REGEX.test(recipient)) {
          result.recipients.push({ name: recipient, email: recipient });
        } else {
          result.invalid.push(recipient);
        }

        return result;
      },
      { recipients: [], invalid: [] }
    );
};

const formatBulkRecipients = recipients => {
  return recipients.reduce((text, recipient) => {
    return `${text}"${recipient.name}" <${recipient.email}>, `;
  }, '');
};

export default {
  name: 'RecipientList',
  components: {
    BFormTextarea,
    ZenFormGroup,
    BCol,
    BFormInput,
    BFormRow,
  },
  props: {
    value: {
      type: Array,
      required: true,
    },
    validated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      text: '',
      invalid: [],
    };
  },
  computed: {
    ...mapGetters(['featureFlags']),
    deleteButtonClass() {
      return ['delete-button', { 'is-disabled': this.value.length === 1 }];
    },
  },
  created() {
    this.restoreRecipients();
  },
  methods: {
    onBulkPaste(event) {
      const text = (event.clipboardData || window.clipboardData).getData('text');
      const { recipients, invalid } = getBulkRecipientData(text);

      const formattedRecipients = formatBulkRecipients(recipients);
      const formattedInvalid = invalid.join(', ');

      this.text = `${formattedRecipients} ${formattedInvalid}`;
      this.invalid = invalid;
      this.$emit('input', recipients);
    },
    onBulkBlur() {
      const { recipients, invalid } = getBulkRecipientData(this.text);
      this.invalid = invalid;
      this.$emit('input', recipients);
    },
    restoreRecipients() {
      // restore recipients value if there's any already set
      if (this.value.length && !this.text) this.text = formatBulkRecipients(this.value);
    },
    addRecipient() {
      this.$emit('input', [...this.value, { name: '', email: '' }]);
    },
    removeRecipient(index) {
      if (this.value.length <= 1) {
        return;
      }
      const newRecipients = cloneDeep(this.value);
      newRecipients.splice(index, 1);
      this.$emit('input', newRecipients);
    },
    editRecipient(index, property, newValue) {
      const newRecipients = cloneDeep(this.value);
      newRecipients[index][property] = newValue;
      this.$emit('input', newRecipients);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/design/colors';

.warning {
  color: $color-my-sin;
  margin-top: 5px;
}

// overriding with important because of bootstrap
.invalid {
  border-color: $color-amaranth !important;

  &:focus {
    box-shadow: 0 0 0 0.2rem transparentize($color-amaranth, 0.85) !important;
  }
}

.recipient-list {
  overflow: auto;
  overflow-x: hidden;
  max-height: 143px;
  padding: 0 10px;
  margin: 0 -10px;
  .form-row:not(:first-child) {
    margin-bottom: 10px;
  }
  .delete-button {
    display: inline-block;
    line-height: 17px;
    margin: 10px 0;
    user-select: none;
    &.is-disabled {
      pointer-events: none;
      color: $color-oslo-gray;
    }
  }
}
</style>
